import { Outlet } from "react-router";
import { useEffect } from "react";
import { useAuthContext } from "@acto/web-common/auth";
import { css } from "@pigment-css/react";
import { basicLogger, LDContext, LDProvider, useLDClient } from "launchdarkly-react-client-sdk";
import { ActoGraphqlProvider } from "@acto/taskpane/core/data-access/graphql/ActoGraphqlProvider";
import { ErrorBoundary } from "react-error-boundary";
import { useFlagWithContext } from "@acto/web-common/feature-flags";
import { usePlatformName } from "@acto/taskpane/core/platform/PlatformNameContext";
import { AnalyticsProvider } from "@acto/taskpane/core/analytics";
import { SessionTelemetryContextSync } from "@acto/taskpane/core/telemetry/SessionTelemetryProvider";
import { ChatEnvelopeProvider } from "@acto/taskpane/core/chat-envelope";
import Intercom from "@intercom/messenger-js-sdk";

const WaitForLaunchDarklyClient = ({ children }: React.PropsWithChildren) => {
  const ldClient = useLDClient();

  if (!ldClient) {
    return null;
  }

  return children;
};

const globalStyles = css((theme) => ({
  fontFamily: theme.theme.font.family,
  width: "100%",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  "& p": {
    margin: 0,
  },
}));

const IntercomInitializer = () => {
  const { user } = useAuthContext();
  const shouldUseIntercom = useFlagWithContext("use-intercom", false);

  useEffect(() => {
    if (!shouldUseIntercom) {
      return;
    }

    if (user && user.email && user.name) {
      Intercom({
        app_id: "pchdzcsx",
        name: user.name,
        email: user.email,
      });
    }
  }, [user, shouldUseIntercom]);

  return null;
};

const Layout = () => {
  const { user, accessToken } = useAuthContext();
  const platformName = usePlatformName();
  const launchDarklyContext: LDContext | undefined = user
    ? {
        kind: "multi",
        user: {
          key: user.id,
          email: user.email,
          name: user.name,
          roles: user.roles,
        },
        tenant: {
          key: user.tenantId,
        },
      }
    : undefined;

  return (
    <ErrorBoundary fallback={<div>Something went wrong</div>}>
      <ActoGraphqlProvider
        accessToken={accessToken}
        graphqlEndpoint={import.meta.env["VITE_ACTO_API_GRAPHQL_ENDPOINT"]}
        wsEndpoint={import.meta.env["VITE_ACTO_API_GRAPHQL_WS_ENDPOINT"]}
      >
        <LDProvider
          clientSideID={import.meta.env["VITE_LAUNCHDARKLY_CLIENT_SIDE_ID"]}
          context={launchDarklyContext}
          options={{ logger: basicLogger({ level: "warn" }) }}
        >
          <WaitForLaunchDarklyClient>
            <IntercomInitializer />
            <AnalyticsProvider app="widget" platform={platformName}>
              <ChatEnvelopeProvider>
                <div className={globalStyles}>
                  <SessionTelemetryContextSync />
                  <Outlet />
                </div>
              </ChatEnvelopeProvider>
            </AnalyticsProvider>
          </WaitForLaunchDarklyClient>
        </LDProvider>
      </ActoGraphqlProvider>
    </ErrorBoundary>
  );
};

export default Layout;
